import React from "react";
import {Accordion, Avatar, Badge, Box, Button, Flex, rem, Text, Tooltip} from "@mantine/core";
import {IconMapPin, IconSparkles} from "@tabler/icons-react";
import {Match} from "lib/types";
import {summarizeAvailability, summarizeDaysAvailability, summarizePeriodsAvailability} from "lib/utils";

interface Props {
    match: Match
    staticBaseUrl: string
}

export default function MatchAccordionItem({match, staticBaseUrl}: Props) {
    const pk = match.volunteer.pk
    const person = match.volunteer.fields
    console.log(person)
    const availabilityDays = summarizeDaysAvailability(person.availability);
    const availabilityPeriods = summarizePeriodsAvailability(person.availability);

    const matchType = match.score > 90 ? '#Excellent' : match.score > 50 ? '#Good' : '#Fair';
    const aiMatchType = match.score > 90 ? 'Great' : match.score > 50 ? 'Good' : 'Fair';
    const matchTypeColor = match.score > 90 ? '#249C4D' : match.score > 50 ? '#15A4DA' : '#E11B88';

    const genderPreference =  person.gender_preference.charAt(0).toUpperCase() + person.gender_preference.slice(1)
    const dementiaOk = person.fine_with_dementia ? 'Dementia OK' : 'Dementia NOT OK';
    const petAllergy = person.pet_allergies_str
    const petFears = person.pet_fears_str
    const additionalLanguages = person.additional_languages?.split(',').map(lang => `${lang.trim()} L.`) ?? [];

    const generalInformationItems = [
        person.fine_with_smoking ? 'Smoker' : 'Non Smoker',
        petAllergy ? 'Pet allergy' : undefined,
        petFears ? 'Pet fear' : undefined,
        genderPreference ? `${genderPreference} Older Person` : undefined,
        dementiaOk,
        ...additionalLanguages
    ].filter((item): item is string => item !== undefined);

    const TRANSPORT_TYPE_LABELS: Record<string, string> = {
        'walking': 'Walk',
        'bicycling': 'Bicycle',
        'driving': 'Car',
        'transit': 'Public'
    };

    function generateRingfencedInfo(datetimeStr?: string) {
      if (!datetimeStr) {
          return 'Temporary match held for OP';
      }

      const now = new Date();
      const targetDate = new Date(datetimeStr);

      // @ts-ignore
      const diffTime = now - targetDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      const formattedDate = targetDate.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric'
      });

      const cleanedDate = formattedDate.replace(',', '');

      return `${diffDays} days  | ${cleanedDate}\nTemporary match held for OP`;
    }


    return (
        <Accordion.Item key={match.id} value={match.id.toString()}>
          <Accordion.Control>
            <Flex direction='row' gap={rem(20)}>

                {
                    !person.ringfenced ? (
                        <Avatar
                            color={'initials'}
                            name={person.name}
                            radius="xl"
                        />
                    ) :
                        (
                        <Tooltip
                            multiline
                            label={generateRingfencedInfo(person.ringfenced_date ?? undefined)}
                            w={rem(220)}
                        >
                            <Box pos={'relative'}>
                                <Avatar
                                    color={'initials'}
                                    name={person.name}
                                    radius="xl"
                                    style={{ border: person.ringfenced ? '2px solid #E1251B': 'none'}}
                                />

                                <Box
                                    bg={'#E1251B'}
                                    style={{
                                        border: '2px solid #FFFFFF',
                                        borderRadius: '50%',
                                        width: rem(18),
                                        height: rem(18),
                                        position: 'absolute',
                                        top: rem(24),
                                        right: rem(-3),
                                        display: person.ringfenced ? 'block' : 'none'
                                }}>
                                    <img src={`${staticBaseUrl}alone/images/ringfenced-icon.svg`} alt="Ringfenced"
                                    style={{width: '9px', height: '21px', marginLeft: '2.5px', marginTop: '-6px'}}/>
                                </Box>
                            </Box>
                        </Tooltip>
                    )
                }

                <Flex direction='column' gap={rem(8)} flex={1} pr={rem(8)}>
                    <Text
                        c={'#1A1D1F'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                    >
                        {person.name}
                    </Text>
                    <Flex direction='row' justify={'space-between'} align={'center'}>
                        <Text
                            fw={600}
                            fz={rem(13)}
                            lh={rem(16)}
                            c={'#7C7F82'}
                            lineClamp={5}
                            maw={rem(100)}
                        >
                            {person.full_address}
                        </Text>
                        <Button
                            size={'sm'}
                            bg={'#FCFCFC'}
                            styles={{root: {borderColor: '#EFEFEF', borderWidth: '2px'}}}
                            radius={rem(8)}
                            py={rem(4)}
                            px={rem(8)}
                            component='a'
                            href={`/map/volunteer/${pk}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Flex direction='row' align={'center'} gap={rem(4)}>
                                <IconMapPin color={'#1A1D1F'} stroke={2} size={20} />
                                <Text
                                    c={'#1A1D1F'}
                                    fz={rem(12)}
                                    lh={rem(24)}
                                    fw={500}>
                                    <b>{match.travel_time__travel_minutes} min</b> {TRANSPORT_TYPE_LABELS[match.volunteer.fields.transport_type ?? ''] ?? 'No transport'}
                                </Text>
                            </Flex>

                        </Button>
                    </Flex>

                    <Flex direction={'row'} gap={rem(14)} align={'center'} mt={rem(8)}>
                      <Badge
                          c={person.days_waiting > 180 ? '#E1251B' : person.days_waiting > 90 ? '#F59E0B' : '#272B30'}
                          bg={person.days_waiting > 180 ? '#F9E6E5' : person.days_waiting > 90 ? '#FEF3C7' : '#EFEFEF'}
                          fw={600}
                          fz={rem(14)}
                          lh={rem(22)}
                          tt={'capitalize'}
                          p={rem(8)}
                          radius="sm"
                          size={'lg'}
                      >{person.days_waiting}</Badge>

                        <Badge
                          c={'#1A1D1F'}
                          bg={'#EFEFEF'}
                          fw={600}
                          fz={rem(14)}
                          lh={rem(22)}
                          tt={'capitalize'}
                          p={rem(8)}
                          radius="sm"
                          size={'lg'}
                      >{person.gender}</Badge>

                          <Badge
                          c={'#fff'}
                          bg={matchTypeColor}
                          fw={600}
                          fz={rem(14)}
                          lh={rem(22)}
                          tt={'capitalize'}
                          p={rem(8)}
                          radius="sm"
                          size={'lg'}
                      >{matchType}</Badge>
                    </Flex>
                </Flex>
            </Flex>
          </Accordion.Control>
          <Accordion.Panel>
              <Flex direction='column' gap={rem(20)}>
                <Flex direction={'row'} gap={rem(20)}>
                <Box miw={rem(38)}></Box>

                {/*Actual content*/}
                <Flex direction={'column'} gap={rem(16)}>
                    <Flex direction='row' gap={rem(12)}>
                        {/*Headers*/}
                        <Flex direction='column' gap={rem(4)}>
                            <Text c={'#272B30'} fz={rem(13)} lh={rem(16)} fw={600}>Age</Text>
                            <Text c={'#272B30'} fz={rem(13)} lh={rem(16)} fw={600}>Days</Text>
                            <Text c={'#272B30'} fz={rem(13)} lh={rem(16)} fw={600}>Time</Text>
                        </Flex>

                        {/*Values*/}
                        <Flex direction='column' gap={rem(4)}>
                            <Text c={'#6F767E'} fz={rem(13)} lh={rem(16)} fw={600}>{person.age}</Text>
                            <Text c={'#6F767E'} fz={rem(13)} lh={rem(16)} fw={600}>{availabilityDays}</Text>
                            <Text c={'#6F767E'} fz={rem(13)} lh={rem(16)} fw={600}>{availabilityPeriods}</Text>
                        </Flex>
                    </Flex>

                    <Flex direction={'row'} gap={rem(8)} wrap={'wrap'}>
                        {generalInformationItems.map((item, index) => (
                            <Badge
                              key={index}
                              px={rem(8)}
                              py={rem(4)}
                              c={'#272B30'}
                              bg={'#EFEFEF'}
                              fw={600}
                              fz={rem(12)}
                              lh={rem(16)}
                              tt={'capitalize'}
                              radius="sm"
                              size={'lg'}
                            >{item}</Badge>
                        ))}
                    </Flex>

                    <Flex
                        direction='column'
                        gap={rem(12)}
                        bg={'#EFEFEFCC'}
                        style={{borderRadius: 12}}
                        px={rem(12)}
                        py={rem(17)}
                    >
                        <Flex direction='row' gap={rem(6)} align='center'>
                            <IconSparkles stroke={2} size={16} color={'#249C4D'}/>
                            <Text fw={600} fz={rem(13)} lh={rem(16)} c={'#1A1D1F'}>
                                { aiMatchType } Match!
                            </Text>
                        </Flex>

                        <Text
                            fw={500}
                            c={'#272B30'}
                            fz={rem(13)}
                            lh={rem(18)}
                            lineClamp={30}
                        >
                            { person.shared_interests_reasoning }
                        </Text>
                    </Flex>

                    <Flex direction='column' gap={rem(10)}>
                        <Text
                            fw={600}
                            fz={rem(13)}
                            lh={rem(16)}
                            c={'#6F767E'}
                        >
                            Also matches OPs
                        </Text>
                        <Box px={rem(6)}>
                            <Avatar.Group>
                                {
                                    person.matches__names.map((name, index) => (
                                        // @ts-ignore
                                      <Tooltip label={name.older_person__name} withArrow>
                                          {/*@ts-ignore*/}
                                        <Avatar color={'initials'} name={name.older_person__name} radius="xl" />
                                      </Tooltip>
                                    ))
                                }

                                { person.matches_count > 5 && (<Avatar>+{ person.matches_count - 5}</Avatar>)}
                            </Avatar.Group>
                        </Box>
                    </Flex>
                </Flex>
              </Flex>
                <Button
                    w={'100%'}
                    size={'md'}
                    bg={'#FCFCFC'}
                    mt={rem(6)}
                    styles={{root: {borderColor: '#EFEFEF', borderWidth: '2px'}}}
                    radius={rem(8)}
                    component='a'
                    href={`https://bconnectplatform.lightning.force.com/lightning/r/Contact/${person.salesforce_id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Text
                        c={'#1A1D1F'}
                        fz={rem(14)}
                        lh={rem(24)}
                        fw={600}
                    >
                        Contact Info
                    </Text>
                </Button>
              </Flex>
          </Accordion.Panel>
        </Accordion.Item>
    )
}